import React, { memo } from 'react';
import { useArAppealSelectionsStore } from '@xtnd/web/shared/stores';

interface AppealCheckboxCellProps {
  invoiceId: string;
}

/**
 * Memoized checkbox cell component that subscribes directly to the Zustand store.
 * This prevents the entire table column definition from re-rendering when selection changes.
 * Only this individual cell re-renders when its selection state changes.
 */
export const AppealCheckboxCell = memo(({ invoiceId }: AppealCheckboxCellProps) => {
  const selectedKeys = useArAppealSelectionsStore(state => state.selectedKeys);
  const toggleSelection = useArAppealSelectionsStore(state => state.toggleSelection);

  const isChecked = selectedKeys.includes(invoiceId);

  return (
    <input
      type="checkbox"
      className="h-4 w-4 text-xtnd-blue border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer"
      checked={isChecked}
      onChange={() => toggleSelection(invoiceId)}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
    />
  );
});

AppealCheckboxCell.displayName = 'AppealCheckboxCell';

interface AppealCheckboxHeaderProps {
  currentPageInvoiceIds: string[];
}

/**
 * Memoized checkbox header component for select-all functionality.
 * Subscribes directly to store to avoid column re-renders.
 */
export const AppealCheckboxHeader = memo(({ currentPageInvoiceIds }: AppealCheckboxHeaderProps) => {
  const selectedKeys = useArAppealSelectionsStore(state => state.selectedKeys);
  const selectAll = useArAppealSelectionsStore(state => state.selectAll);
  const clearSelections = useArAppealSelectionsStore(state => state.clearSelections);

  // Check if any items on current page are selected (for indeterminate/checked state)
  const hasSelections = currentPageInvoiceIds.length > 0 && selectedKeys.length > 0;

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      selectAll(currentPageInvoiceIds);
    } else {
      clearSelections();
    }
  };

  return (
    <input
      type="checkbox"
      className="h-4 w-4 text-xtnd-blue border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-xtnd-blue cursor-pointer"
      checked={hasSelections}
      onChange={handleSelectAll}
      onClick={e => e.stopPropagation()}
    />
  );
});

AppealCheckboxHeader.displayName = 'AppealCheckboxHeader';
